import { yupResolver } from '@hookform/resolvers/yup';
import { disableCache, Icon } from '@iconify/react';
import { Box, Dialog, DialogContent, Grid, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';

import * as Yup from 'yup';
import {
  addRatecard,
  clearRateCard,
  clearTourList,
  getRatecardDetails,
  getTourNames,
  getVehicleSeaters,
  updateRatecard
} from '../../../../../redux/reducer/autoPlannerSlices/rateCardSlice';
import { useAppDispatch, useAppSelector } from '../../../../../../../app/redux/hooks';
import CustomTextField from '../../../../../../../common/components/customized/customtextfield/CustomTextField';
import CustomButton from '../../../../../../../common/components/buttons/CustomButton';
import { EditableTable } from './EditableTable';
import CustomCurrencyField from '../../../../../../../common/components/customcurrency/CustomCurrency';
import dayjs from 'dayjs';
import { updateToast } from '../../../../../../../common/redux/reducer/commonSlices/toastSlice';

type AddOn = {
  addOnId?: any;
  name?: string;
  addCost?: number;
  startTime?: number;
  endTime?: number;
};

type Seater = {
  seaterId?: any;
  seater: number;
  baseCost: number;
  addOns: AddOn[];
};

type Tour = {
  tourId?: any;
  tourName: string;
  mode: string;
  isAddOn?: boolean;
  seaters: Seater[];
};

type Modes = {
  mode: string;
  isApiCall?: boolean;
  tours: Tour[];
};

type RateCardData = {
  rateCardId?: any;
  rateCardName: string;
  currency: string;
  modes: Modes[];
};

const AddRatecard = ({
  isOpen,
  setIsOpen,
  setIsSelected,
  isSelected,
  selectedCard,
  setSelectedCard,
  clearState
}: any) => {
  const seatersList = useAppSelector(state => state.getSeatersListReducer);
  const toursList = useAppSelector(state => state.getToursListReducer);
  const { isLoading: addIsLading } = useAppSelector(state => state.addRateCardDetails);
  const { isLoading: getTourLoading } = useAppSelector(
    state => state.getToursListReducer
  );
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const { isLoading: updateIsLoading } = useAppSelector(
    state => state.updateRatecardDeatils
  );
  const [isFirstCall, setIsFirstCall] = useState(true);
  const modes = useMemo(() => ['SIC', 'TSIC', 'PVT', 'GRP'], []);

  const [modeValue, setModeValue] = useState('SIC');

  const dispatch = useAppDispatch();

  const seaterColumns: ColumnDef<Tour>[] = useMemo(
    () =>
      seatersList?.data
        ?.map((seaterNum: any) => ({
          id: `${seaterNum}`,
          header: `Seater ${seaterNum}`,
          accessorFn: (row: any) => {
            const seaterData = row.seaters?.find(
              (s: { seater: any }) => s.seater === seaterNum
            );
            return seaterData?.baseCost ?? '';
          },
          cell: (info: any) => {
            const value = info.getValue();
            return <div>{value}</div>;
          }
        }))
        .sort((a: any, b: any) => a.id - b.id) ?? [],
    [seatersList?.data]
  );

  const columns: ColumnDef<Tour>[] = useMemo(
    () => [
      {
        accessorKey: 'tourName',
        id: 'tourName',
        header: 'Tour Name'
      },
      ...seaterColumns
    ],
    [seaterColumns]
  );

  const handleModeChange = async (event: React.SyntheticEvent, newValue: string) => {
    const currentIndex = modes.indexOf(newValue);

    const isApiCall = modeFields[currentIndex]?.isApiCall;
    setModeValue(newValue);
    if (!isApiCall) {
      let data: any = await dispatch(getTourNames(newValue));
      data =
        newValue === 'PVT'
          ? [
              'AIRPORT ARRIVAL',
              'AIRPORT DEPARTURE',
              'MIDNIGHT TRANSFER',
              ...data?.payload?.data
            ]
          : data?.payload?.data;

      if (!isSelected) {
        updateTour(currentIndex, {
          mode: newValue,
          isApiCall: true,
          tours: data?.map((tour: any) => ({
            tourName: tour,
            mode: newValue,
            isAddOn: true,
            seaters: seatersList?.data
              ?.map((seater: any) => {
                return {
                  seater: seater,
                  baseCost: 0,
                  currentBaseCost: 0,
                  addOns: [
                    {
                      name: '',
                      addCost: 0,
                      startTime: null,
                      endTime: null
                    }
                  ],
                  prevaddOns: [
                    {
                      name: '',
                      addCost: 0,
                      startTime: null,
                      endTime: null
                    }
                  ]
                };
              })
              .sort((a: any, b: any) => a.seater - b.seater)
          }))
        });
      }

      if (isSelected) {
        updateTour(currentIndex, {
          mode: newValue,
          isApiCall: true,
          tours: data?.map((tour: any) => {
            const matchedTour = selectedCard?.tourResponses?.find(
              (t: any) =>
                t.tourName.toLowerCase() == tour.toLowerCase() &&
                t.mode.toLowerCase() == newValue.toLowerCase()
            );

            return {
              tourId: matchedTour ? matchedTour?.tourId : null,
              tourName: tour,
              mode: newValue,
              isAddOn: true,
              seaters: seatersList?.data
                ?.map((seater: any) => {
                  const matchedSeater = matchedTour?.seaterResponses?.find(
                    (s: any) => s.seater == seater
                  );
                  return {
                    seaterId: matchedSeater ? matchedSeater?.seaterId : null,
                    seater,
                    baseCost: matchedSeater ? matchedSeater.baseCost : 0,
                    currentBaseCost: matchedSeater ? matchedSeater.baseCost : 0,
                    prevaddOns:
                      matchedSeater?.rateCardOnResponses?.length > 0
                        ? matchedSeater.rateCardOnResponses.map((addon: any) => ({
                            addOnId: addon?.rateCardOnId,
                            name: addon.rateCardOnName || '',
                            addCost: addon.addCost || 0,
                            startTime: new Date(addon.startTime * 1000 || 0),
                            endTime: new Date(addon.endTime * 1000 || 0)
                          }))
                        : [
                            {
                              name: '',
                              addCost: 0,
                              startTime: 0,
                              endTime: 0
                            }
                          ],
                    addOns:
                      matchedSeater?.rateCardOnResponses.length > 0
                        ? matchedSeater.rateCardOnResponses.map((addon: any) => ({
                            addOnId: addon?.rateCardOnId,
                            name: addon.name ?? '',
                            addCost: addon.addCost ?? 0,
                            startTime: new Date(addon.startTime * 1000 || 0),
                            endTime: new Date(addon.endTime * 1000 || 0)
                          }))
                        : [
                            {
                              name: '',
                              addCost: 0,
                              startTime: null,
                              endTime: null
                            }
                          ]
                  };
                })
                .sort((a: any, b: any) => a.seater - b.seater)
            };
          })
        });
      }
    }
    modeFields?.forEach((modes: any, modeIndex: number) => {
      if (modes?.mode !== newValue) {
        updateTour(modeIndex, {
          ...modeFields[modeIndex],
          tours: modeFields[modeIndex]?.tours?.map((tour: any) => ({
            ...tour,
            seaters: tour?.seaters?.map((seater: any) => ({
              ...seater,
              baseCost: seater?.currentBaseCost
            }))
          }))
        });
      }
    });
  };

  const ratecardSchema = Yup.object().shape({
    rateCardName: Yup.string().required('Enter rate card name'),
    currency: Yup.string().required('Enter currency'),
    modes: Yup.array()
      .of(
        Yup.object().shape({
          mode: Yup.string().required(),
          tours: Yup.array()
            .of(
              Yup.object().shape({
                tourName: Yup.string().required(),
                mode: Yup.string().required(),
                isAddOn: Yup.boolean(),
                seaters: Yup.array()
                  .of(
                    Yup.object().shape({
                      seater: Yup.number().required(),
                      baseCost: Yup.number()
                        .required('Enter base cost')
                        .typeError('Enter base cost'),
                      addOns: Yup.array().of(
                        Yup.object().shape({
                          name: Yup.string(),
                          addCost: Yup.number()
                            .typeError('Enter valid cost')
                            .notRequired(),
                          startTime: Yup.date().notRequired(),
                          endTime: Yup.date().notRequired()
                        })
                      )
                    })
                  )
                  .required()
              })
            )
            .required()
        })
      )
      .required()
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    reset,
    getValues,
    trigger,
    formState: { errors },
    setError
  } = useForm<any>({
    resolver: yupResolver(ratecardSchema as any),
    defaultValues: {
      rateCardName: '',
      currency: '',
      modes: []
    }
  });

  const { fields: modeFields, update: updateTour }: any = useFieldArray({
    control,
    name: 'modes'
  });
  const handleDialogClose = () => {
    reset();
    clearErrors();
    setIsOpen(false);
    setIsSelected(null);
    setSelectedCard(null);
  };

  const buildRateCardPayload = (formData: any) => {
    const tours = formData.modes.flatMap((mode: any) =>
      mode.tours
        .map((tour: any) => ({
          tourId: tour?.tourId,
          tourName: tour.tourName,
          mode: tour.mode,
          isAddOn: tour.isAddOn,
          seaters: tour.seaters
            .filter((seater: any) => Number(seater.baseCost) > 0)
            .map((seater: any) => ({
              seaterId: seater?.seaterId,
              seater: Number(seater.seater),
              baseCost: Number(seater.baseCost),
              addOns: seater.prevaddOns
                ?.filter((addOn: any) => Number(addOn.addCost) > 0)
                ?.map((addOn: any) => ({
                  addOnId: addOn.addOnId,
                  name: addOn.name || undefined,
                  addCost: Number(addOn.addCost),
                  startTime: Math.floor(new Date(addOn.startTime).getTime() / 1000),
                  endTime: Math.floor(new Date(addOn.endTime).getTime() / 1000)
                }))
            }))
        }))
        .filter((tour: any) => tour.seaters.length > 0)
    );

    return {
      rateCardId: selectedCard?.rateCardId || undefined,
      rateCardName: formData.rateCardName,
      currency: formData.currency,
      tours
    };
  };

  const onSubmit = async (data: RateCardData) => {
    const payload = buildRateCardPayload(data);
    let res;
    if (payload.tours?.length === 0) {
      dispatch(
        updateToast({
          show: true,
          message: 'Base cost is needed for atleast one tour and seaters',
          severity: 'warning'
        })
      );

      return;
    }
    if (isSelected) {
      res = await dispatch(updateRatecard(payload));
    } else {
      res = await dispatch(addRatecard(payload));
    }

    if (res?.meta?.requestStatus === 'fulfilled') {
      reset();
      clearErrors();
      setIsOpen(false);
      setIsSelected(null);
      handleDialogClose();

      const data = { pageNo: 1, pageSize: 12 };
      dispatch(clearRateCard());
      clearState();
      await dispatch(getRatecardDetails(data));
    }
  };

  const currentModeData = useMemo(() => {
    const modeIndex = modes.indexOf(modeValue);
    return modeFields[modeIndex]?.tours ?? [];
  }, [modeFields, modes, modeValue]);

  const currentModeIndex = useMemo(() => modes.indexOf(modeValue), [modes, modeValue]);

  useEffect(() => {
    dispatch(getTourNames('SIC'));
    dispatch(getVehicleSeaters());

    return () => {
      dispatch(clearTourList());
    };
  }, []);

  useEffect(() => {
    if (
      !toursList?.data?.length ||
      !seatersList?.data?.length ||
      !isFirstCall ||
      isSelected
    )
      return;

    modes.forEach((mode, modeIndex) => {
      if (mode === 'SIC') {
        updateTour(0, {
          mode,
          isApiCall: true,
          tours: toursList?.data?.map((tour: any) => ({
            tourName: tour,
            mode,
            isAddOn: true,
            seaters: seatersList?.data
              ?.map((seater: any) => {
                return {
                  seater: Number(seater),
                  baseCost: 0,
                  currentBaseCost: 0,
                  addOns: [
                    {
                      name: '',
                      addCost: 0,
                      startTime: null,
                      endTime: null
                    }
                  ],
                  prevaddOns: [
                    {
                      name: '',
                      addCost: 0,
                      startTime: null,
                      endTime: null
                    }
                  ]
                };
              })
              ?.sort((a: any, b: any) => a.seater - b.seater)
          }))
        });
      } else {
        updateTour(modeIndex, {
          mode,
          isApiCall: false,
          tours: []
        });
      }
    });

    setIsFirstCall(false);
  }, [toursList, seatersList, !isSelected, isFirstCall]);

  //update useEffect
  useEffect(() => {
    if (
      !toursList?.data?.length ||
      !seatersList?.data?.length ||
      !isFirstCall ||
      !isSelected
    )
      return;

    modes.forEach((mode, modeIndex) => {
      if (mode === 'SIC') {
        updateTour(modeIndex, {
          mode,
          isApiCall: true,
          tours: toursList?.data?.map((tour: any) => {
            const matchedTour = selectedCard?.tourResponses?.find(
              (t: any) =>
                t.tourName.toLowerCase() == tour.toLowerCase() &&
                t.mode.toLowerCase() == mode.toLowerCase()
            );
            return {
              tourId: matchedTour ? matchedTour?.tourId : null,
              tourName: tour,
              mode,
              isAddOn: true,
              seaters: seatersList?.data
                ?.map((seater: any) => {
                  const matchedSeater = matchedTour?.seaterResponses?.find(
                    (s: any) => s.seater == seater
                  );

                  return {
                    seaterId: matchedSeater ? matchedSeater?.seaterId : null,
                    seater,
                    baseCost: matchedSeater ? matchedSeater.baseCost : 0,
                    currentBaseCost: matchedSeater ? matchedSeater.baseCost : 0,
                    prevaddOns:
                      matchedSeater?.rateCardOnResponses?.length > 0
                        ? matchedSeater.rateCardOnResponses.map((addon: any) => ({
                            addOnId: addon?.rateCardOnId,
                            name: addon.rateCardOnName || '',
                            addCost: addon.addCost || 0,
                            startTime: new Date(addon.startTime * 1000 || 0),
                            endTime: new Date(addon.endTime * 1000 || 0)
                          }))
                        : [
                            {
                              name: '',
                              addCost: 0,
                              startTime: 0,
                              endTime: 0
                            }
                          ],
                    addOns:
                      matchedSeater?.rateCardOnResponses?.length > 0
                        ? matchedSeater.rateCardOnResponses.map((addon: any) => ({
                            addOnId: addon?.rateCardOnId,
                            name: addon.rateCardOnName || '',
                            addCost: addon.addCost || 0,
                            startTime: new Date(addon.startTime * 1000 || 0),
                            endTime: new Date(addon.endTime * 1000 || 0)
                          }))
                        : [
                            {
                              name: '',
                              addCost: 0,
                              startTime: 0,
                              endTime: 0
                            }
                          ]
                  };
                })
                ?.sort((a: any, b: any) => a.seater - b.seater)
            };
          })
        });
      } else {
        updateTour(modeIndex, {
          mode: mode,
          isApiCall: false,
          tours: selectedCard?.tourResponses
            ?.map((item: any) => item.mode)
            ?.includes(mode)
            ? selectedCard?.tourResponses
                .filter((modes: any) => modes.mode === mode)
                .map((item: any) => {
                  return {
                    tourId: item.tourId || null,
                    tourName: item.tourName,
                    mode: item.mode,
                    isAddOn: true,
                    seaters: item.seaterResponses
                      .map((item1: any) => ({
                        seaterId: item1.seaterId || null,
                        seater: item1.seater,
                        baseCost: item1.baseCost || 0,
                        currentBaseCost: item1.baseCost || 0,
                        prevaddOns:
                          item1?.rateCardOnResponses?.length > 0
                            ? item1.rateCardOnResponses.map((addon: any) => ({
                                addOnId: addon?.rateCardOnId,
                                name: addon.rateCardOnName || '',
                                addCost: addon.addCost || 0,
                                startTime: new Date(addon.startTime * 1000 || 0),
                                endTime: new Date(addon.endTime * 1000 || 0)
                              }))
                            : [
                                {
                                  name: '',
                                  addCost: 0,
                                  startTime: 0,
                                  endTime: 0
                                }
                              ],
                        addOns:
                          item1?.rateCardOnResponses?.length > 0
                            ? item1.rateCardOnResponses.map((addon: any) => ({
                                addOnId: addon?.rateCardOnId || '',
                                name: addon.rateCardOnName || '',
                                addCost: addon.addCost || 0,
                                startTime: addon.startTime
                                  ? new Date(addon.startTime * 1000 || 0)
                                  : 0,
                                endTime: addon.endTime
                                  ? new Date(addon.endTime * 1000 || 0)
                                  : 0
                              }))
                            : [
                                {
                                  name: '',
                                  addCost: 0,
                                  startTime: 0,
                                  endTime: 0
                                }
                              ]
                      }))
                      ?.sort((a: any, b: any) => a.seater - b.seater)
                  };
                })
            : []
        });
      }
    });

    setValue('rateCardId', selectedCard?.rateCardId);
    setValue('rateCardName', selectedCard?.rateCardName);
    setValue('currency', selectedCard?.currency);
    setIsFirstCall(false);
  }, [toursList, seatersList, isSelected, isFirstCall]);

  return (
    <Dialog open={isOpen} maxWidth='xl' fullWidth hideBackdrop sx={{ zIndex: 1 }}>
      <DialogContent className='addratecard-container'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Typography fontWeight={700} color={'#26134b'}>
            {isSelected ? 'Update Rate Card' : 'Add Rate Card'}
          </Typography>

          <Box
            className='addratecard-close'
            sx={{ position: 'absolute', top: 10, right: 10, zIndex: 3 }}
          >
            <Icon
              icon='ic:round-close'
              className='ratecard-close-icon'
              onClick={handleDialogClose}
            />
          </Box>

          <Grid container spacing={2} mt={0.5}>
            <Grid item md={2.5} sm={12} xs={12}>
              <CustomTextField
                label='Rate Card Name'
                control={control}
                name='rateCardName'
                id='rateCardName'
                placeholder='Rate card name'
              />
            </Grid>
            <Grid item md={2.5} sm={12} xs={12}>
              <CustomCurrencyField
                control={control}
                name='currency'
                placeholder='Currency'
                label='Currency'
                setValue={setValue}
                defaultValue={selectedCard?.currency && selectedCard?.currency}
                value={selectedCard?.currency && selectedCard?.currency}
                trigger={trigger}
              />
            </Grid>

            <Grid
              item
              md={6.8}
              sm={12}
              xs={12}
              display={'flex'}
              justifyContent={'flex-end'}
              alignItems={'center'}
              gap={2}
            >
              <CustomButton
                category='Cancel'
                className='cancel'
                onClick={handleDialogClose}
              />
              <CustomButton
                category={isSelected ? 'Update Rate Card' : 'Add Rate Card'}
                className='saveChanges'
                type='submit'
                disabled={editingRow != null}
                loading={updateIsLoading || addIsLading}
              />
            </Grid>
          </Grid>
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={modeValue}
              onChange={handleModeChange}
              textColor='primary'
              indicatorColor='primary'
              aria-label='secondary tabs example'
            >
              {modes?.map((item: string, index: number) => (
                <Tab key={item} value={item} label={item} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ paddingTop: '30px', overflow: 'auto', height: '300px' }}>
            <EditableTable
              columns={columns}
              control={control}
              modeIndex={currentModeIndex}
              modeFields={modeFields}
              data={currentModeData}
              updateTour={updateTour}
              setEditingRow={setEditingRow}
              editingRow={editingRow}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              loading={getTourLoading}
              setError={setError}
              clearErrors={clearErrors}
            />
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRatecard;
